import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
    };

    return {
        container: { 
            backgroundColor: colors.cardBg, 
            borderRadius: '8px', 
            overflow: 'hidden', 
            boxShadow: `0 2px 5px ${colors.shadow}`,
            marginTop: '20px'
        },
        header: {
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            padding: '15px 20px',
            borderBottom: `2px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        title: { 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: colors.text, 
            margin: 0 
        },
        controlsContainer: { 
            display: 'flex', 
            gap: '15px', 
            marginBottom: '20px', 
            flexWrap: 'wrap', 
            alignItems: 'center',
            padding: '15px 20px',
            borderBottom: `1px solid ${colors.border}`
        },
        searchInput: { 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: `1px solid ${colors.border}`, 
            backgroundColor: isDark ? '#333' : '#fff', 
            color: colors.text, 
            minWidth: '200px' 
        },
        filterSelect: { 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: `1px solid ${colors.border}`, 
            backgroundColor: isDark ? '#333' : '#fff', 
            color: colors.text 
        },
        table: { 
            width: '100%', 
            borderCollapse: 'collapse' 
        },
        tableHeader: { 
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa', 
            padding: '15px 10px', 
            borderBottom: `2px solid ${colors.border}` 
        },
        tableHeaderCell: { 
            padding: '12px 8px', 
            textAlign: 'left', 
            fontWeight: 'bold', 
            color: colors.text, 
            cursor: 'pointer', 
            userSelect: 'none', 
            position: 'relative',
            fontSize: '12px',
            whiteSpace: 'nowrap'
        },
        sortableHeader: { 
            display: 'flex', 
            alignItems: 'center', 
            gap: '5px' 
        },
        sortIcon: { 
            fontSize: '10px', 
            opacity: 0.7 
        },
        tableRow: { 
            borderBottom: `1px solid ${colors.border}`, 
            '&:hover': { backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa' } 
        },
        tableCell: { 
            padding: '8px', 
            color: colors.text, 
            verticalAlign: 'top',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '120px'
        },
        siteBadge: {
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            backgroundColor: colors.link,
            color: 'white'
        },
        noData: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic'
        },
        uploadButton: {
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            borderTop: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa'
        },
        paginationInfo: {
            color: colors.subtleText,
            fontSize: '14px'
        },
        paginationControls: {
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
        },
        paginationButton: {
            padding: '6px 12px',
            backgroundColor: colors.cardBg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            minWidth: '40px'
        },
        paginationButtonDisabled: {
            padding: '6px 12px',
            backgroundColor: isDark ? '#333' : '#f5f5f5',
            color: colors.subtleText,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'not-allowed',
            fontSize: '12px',
            minWidth: '40px'
        },
        pageNumbers: {
            display: 'flex',
            gap: '5px',
            alignItems: 'center'
        },
        pageNumber: {
            padding: '6px 10px',
            backgroundColor: colors.cardBg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            minWidth: '30px',
            textAlign: 'center'
        },
        currentPage: {
            padding: '6px 10px',
            backgroundColor: colors.link,
            color: 'white',
            border: `1px solid ${colors.link}`,
            borderRadius: '4px',
            fontSize: '12px',
            minWidth: '30px',
            textAlign: 'center'
        }
    };
};

function ScheduledInspectionTable({ theme, sites, onUpload }) {
    const styles = getStyles(theme);
    const [scheduledInspectionData, setScheduledInspectionData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [siteFilter, setSiteFilter] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(100);

    // Implement client-side pagination since backend doesn't support it
    const getPaginatedData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return scheduledInspectionData.slice(startIndex, endIndex);
    };
    
    const displayData = getPaginatedData();

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const fetchScheduledInspectionData = useCallback((page = 1) => {
        setLoading(true);
        const params = new URLSearchParams();
        
        // Note: Backend has pagination disabled, so we don't send page/page_size
        // params.append('page', page);
        // params.append('page_size', pageSize);
        
        // Add search and filter parameters if they exist
        if (searchTerm) params.append('search', searchTerm);
        if (siteFilter) params.append('site_name', siteFilter);
        
        // Add sorting parameters if they exist
        if (sortField) {
            const ordering = sortDirection === 'desc' ? `-${sortField}` : sortField;
            params.append('ordering', ordering);
        }
        
        api.get(`/scheduled-inspections/?${params.toString()}`)
            .then(res => {
                // Handle both paginated and non-paginated responses
                let data = [];
                let totalCount = 0;
                
                if (Array.isArray(res.data)) {
                    // Non-paginated response (all data at once)
                    data = res.data;
                    totalCount = data.length;
                } else if (res.data.results) {
                    // Paginated response
                    data = res.data.results;
                    totalCount = res.data.count || data.length;
                } else {
                    // Fallback
                    data = [];
                    totalCount = 0;
                }
                
                setScheduledInspectionData(data);
                setTotalCount(totalCount);
                setTotalPages(Math.max(1, Math.ceil(totalCount / pageSize)));
                // Reset to page 1 when data changes (search/filter/sort)
                setCurrentPage(1);
            })
            .catch(err => {
                console.error('Error fetching scheduled inspection data:', err);
                let errorMessage = 'Failed to fetch Scheduled Inspection data.';
                
                if (err.response?.status === 404) {
                    errorMessage = 'Scheduled Inspection endpoint not found. Please check if the backend is running.';
                } else if (err.response?.status === 500) {
                    errorMessage = 'Server error occurred. Please try again later.';
                } else if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
                    errorMessage = 'Cannot connect to the server. Please check if the backend is running.';
                }
                
                setAlert({ show: true, message: errorMessage, type: 'error' });
            })
            .finally(() => setLoading(false));
    }, [pageSize, searchTerm, siteFilter, sortField, sortDirection]);

    useEffect(() => {
        fetchScheduledInspectionData(1);
    }, [fetchScheduledInspectionData]);

    // Pagination functions (client-side)
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    const handleUploadClick = () => {
        onUpload('Raw Data - Sched Insp', 'Contains modex (last 2-3 numbers of buno), inspection name, tsn (hours on aircraft), schd_expndrt (when inspection is due)');
    };

    return (
        <div style={styles.container}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
            
            <div style={styles.header}>
                <h2 style={styles.title}>Scheduled Inspection Data</h2>
                <button onClick={handleUploadClick} style={styles.uploadButton}>
                    Upload New Data
                </button>
            </div>

            <div style={styles.controlsContainer}>
                <input
                    type="text"
                    placeholder="Search by Modex, Serno, Org Code, Phase Step, or Location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
                <select
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                    style={styles.filterSelect}
                >
                    <option value="">All Sites</option>
                    {Array.isArray(sites) && sites.map(site => (
                        <option key={site.id} value={site.name}>{site.name}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <Spinner />
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.tableHeader}>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('site_name')}>
                                        Site
                                        {sortField === 'site_name' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('modex')}>
                                        Modex
                                        {sortField === 'modex' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('serno')}>
                                        Serno
                                        {sortField === 'serno' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('tsn')}>
                                        TSN
                                        {sortField === 'tsn' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('schd_dt')}>
                                        Scheduled Date
                                        {sortField === 'schd_dt' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('schd_expndtr')}>
                                        Hours Due
                                        {sortField === 'schd_expndtr' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('remaining')}>
                                        Remaining
                                        {sortField === 'remaining' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('ph_step_nm')}>
                                        Phase Step
                                        {sortField === 'ph_step_nm' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('intrvl')}>
                                        Interval
                                        {sortField === 'intrvl' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('engr_unit_cd')}>
                                        Engr Unit
                                        {sortField === 'engr_unit_cd' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.length === 0 ? (
                                <tr>
                                    <td colSpan="10" style={styles.noData}>
                                        No Scheduled Inspection data available. Upload data to get started.
                                    </td>
                                </tr>
                            ) : (
                                displayData.map((row, index) => (
                                    <tr key={`${row.site_name}-${row.modex}-${index}`} style={styles.tableRow}>
                                        <td style={styles.tableCell}>
                                            <span style={styles.siteBadge}>{row.site_name}</span>
                                        </td>
                                        <td style={styles.tableCell}>{row.modex}</td>
                                        <td style={styles.tableCell}>{row.serno}</td>
                                        <td style={styles.tableCell}>{row.tsn}</td>
                                        <td style={styles.tableCell}>
                                            {row.schd_dt ? new Date(row.schd_dt).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td style={styles.tableCell}>
                                            {row.schd_expndtr ? row.schd_expndtr.toFixed(1) + ' hrs' : 'N/A'}
                                        </td>
                                        <td style={styles.tableCell}>
                                            {row.remaining !== null && row.remaining !== undefined ? 
                                                row.remaining.toFixed(1) + ' hrs' : 
                                                'N/A'
                                            }
                                        </td>
                                        <td style={styles.tableCell}>{row.ph_step_nm}</td>
                                        <td style={styles.tableCell}>{row.intrvl}</td>
                                        <td style={styles.tableCell}>{row.engr_unit_cd}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
                <div style={styles.paginationContainer}>
                    <div style={styles.paginationInfo}>
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
                    </div>
                    <div style={styles.paginationControls}>
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            style={currentPage === 1 ? styles.paginationButtonDisabled : styles.paginationButton}
                        >
                            Previous
                        </button>
                        
                        <div style={styles.pageNumbers}>
                            {getPageNumbers().map(pageNum => (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    style={pageNum === currentPage ? styles.currentPage : styles.pageNumber}
                                >
                                    {pageNum}
                                </button>
                            ))}
                        </div>
                        
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            style={currentPage === totalPages ? styles.paginationButtonDisabled : styles.paginationButton}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ScheduledInspectionTable;